import { Injectable } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, map, shareReplay, distinctUntilChanged } from 'rxjs';

/**
 * Breakpoint values matching our responsive design system
 */
export const RESPONSIVE_BREAKPOINTS = {
  xs: '(max-width: 479px)',      // Small phones (320px - 479px)
  sm: '(min-width: 480px) and (max-width: 767px)', // Large phones (480px - 767px)
  md: '(min-width: 768px) and (max-width: 1023px)', // Tablets (768px - 1023px)
  lg: '(min-width: 1024px) and (max-width: 1279px)', // Small laptops (1024px - 1279px)
  xl: '(min-width: 1280px)',     // Desktops (1280px+)

  // Convenience breakpoints
  mobile: '(max-width: 767px)',   // xs + sm (all phones)
  tablet: '(min-width: 768px) and (max-width: 1023px)', // md only
  desktop: '(min-width: 1024px)', // lg + xl (laptops and desktops)
} as const;

/**
 * Breakpoint type definition
 */
export type ResponsiveBreakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Service to provide responsive state across the application.
 * Uses Angular CDK's BreakpointObserver to detect viewport changes.
 *
 * @example
 * ```typescript
 * constructor(private responsive: ResponsiveService) {
 *   this.responsive.isMobile$.subscribe(isMobile => {
 *     console.log('Is mobile:', isMobile);
 *   });
 * }
 * ```
 *
 * @example
 * ```html
 * <div *ngIf="responsive.isMobile$ | async">
 *   Mobile layout
 * </div>
 * <div *ngIf="responsive.isDesktop$ | async">
 *   Desktop layout
 * </div>
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ResponsiveService {
  /**
   * Observable that emits true when viewport is mobile (< 768px)
   */
  readonly isMobile$: Observable<boolean>;

  /**
   * Observable that emits true when viewport is tablet (768px - 1023px)
   */
  readonly isTablet$: Observable<boolean>;

  /**
   * Observable that emits true when viewport is desktop (>= 1024px)
   */
  readonly isDesktop$: Observable<boolean>;

  /**
   * Observable that emits true when viewport is extra small (< 480px)
   */
  readonly isXSmall$: Observable<boolean>;

  /**
   * Observable that emits true when viewport is small (480px - 767px)
   */
  readonly isSmall$: Observable<boolean>;

  /**
   * Observable that emits true when viewport is medium (768px - 1023px)
   */
  readonly isMedium$: Observable<boolean>;

  /**
   * Observable that emits true when viewport is large (1024px - 1279px)
   */
  readonly isLarge$: Observable<boolean>;

  /**
   * Observable that emits true when viewport is extra large (>= 1280px)
   */
  readonly isXLarge$: Observable<boolean>;

  /**
   * Observable that emits the current breakpoint name
   */
  readonly currentBreakpoint$: Observable<ResponsiveBreakpoint>;

  /**
   * Observable that emits true when in portrait orientation
   */
  readonly isPortrait$: Observable<boolean>;

  /**
   * Observable that emits true when in landscape orientation
   */
  readonly isLandscape$: Observable<boolean>;

  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(private breakpointObserver: BreakpointObserver) {
    // Mobile breakpoint (phones)
    this.isMobile$ = this.breakpointObserver
      .observe(RESPONSIVE_BREAKPOINTS.mobile)
      .pipe(
        map(state => state.matches),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    // Tablet breakpoint
    this.isTablet$ = this.breakpointObserver
      .observe(RESPONSIVE_BREAKPOINTS.tablet)
      .pipe(
        map(state => state.matches),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    // Desktop breakpoint (laptops and desktops)
    this.isDesktop$ = this.breakpointObserver
      .observe(RESPONSIVE_BREAKPOINTS.desktop)
      .pipe(
        map(state => state.matches),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    // XS breakpoint (small phones)
    this.isXSmall$ = this.breakpointObserver
      .observe(RESPONSIVE_BREAKPOINTS.xs)
      .pipe(
        map(state => state.matches),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    // SM breakpoint (large phones)
    this.isSmall$ = this.breakpointObserver
      .observe(RESPONSIVE_BREAKPOINTS.sm)
      .pipe(
        map(state => state.matches),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    // MD breakpoint (tablets)
    this.isMedium$ = this.breakpointObserver
      .observe(RESPONSIVE_BREAKPOINTS.md)
      .pipe(
        map(state => state.matches),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    // LG breakpoint (small laptops)
    this.isLarge$ = this.breakpointObserver
      .observe(RESPONSIVE_BREAKPOINTS.lg)
      .pipe(
        map(state => state.matches),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    // XL breakpoint (desktops)
    this.isXLarge$ = this.breakpointObserver
      .observe(RESPONSIVE_BREAKPOINTS.xl)
      .pipe(
        map(state => state.matches),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    // Current breakpoint observable
    this.currentBreakpoint$ = this.breakpointObserver
      .observe([
        RESPONSIVE_BREAKPOINTS.xs,
        RESPONSIVE_BREAKPOINTS.sm,
        RESPONSIVE_BREAKPOINTS.md,
        RESPONSIVE_BREAKPOINTS.lg,
        RESPONSIVE_BREAKPOINTS.xl,
      ])
      .pipe(
        map(() => this.getCurrentBreakpoint()),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    // Orientation observables
    this.isPortrait$ = this.breakpointObserver
      .observe(Breakpoints.HandsetPortrait)
      .pipe(
        map(state => state.matches),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    this.isLandscape$ = this.breakpointObserver
      .observe(Breakpoints.HandsetLandscape)
      .pipe(
        map(state => state.matches),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
      );
  }

  /**
   * Check if a specific breakpoint is currently active
   * @param breakpoint The breakpoint to check
   * @returns True if the breakpoint is active
   */
  isBreakpointActive(breakpoint: keyof typeof RESPONSIVE_BREAKPOINTS): boolean {
    return this.breakpointObserver.isMatched(RESPONSIVE_BREAKPOINTS[breakpoint]);
  }

  /**
   * Get the current active breakpoint
   * @returns The current breakpoint name
   */
  getCurrentBreakpoint(): ResponsiveBreakpoint {
    if (this.breakpointObserver.isMatched(RESPONSIVE_BREAKPOINTS.xs)) {
      return 'xs';
    } else if (this.breakpointObserver.isMatched(RESPONSIVE_BREAKPOINTS.sm)) {
      return 'sm';
    } else if (this.breakpointObserver.isMatched(RESPONSIVE_BREAKPOINTS.md)) {
      return 'md';
    } else if (this.breakpointObserver.isMatched(RESPONSIVE_BREAKPOINTS.lg)) {
      return 'lg';
    } else {
      return 'xl';
    }
  }

  /**
   * Observe a custom breakpoint
   * @param query CSS media query string
   * @returns Observable that emits true when the breakpoint matches
   */
  observe(query: string): Observable<boolean> {
    return this.breakpointObserver.observe(query).pipe(
      map(state => state.matches),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Check if current viewport is mobile (synchronous)
   * @returns True if viewport is mobile
   */
  get isMobile(): boolean {
    return this.breakpointObserver.isMatched(RESPONSIVE_BREAKPOINTS.mobile);
  }

  /**
   * Check if current viewport is tablet (synchronous)
   * @returns True if viewport is tablet
   */
  get isTablet(): boolean {
    return this.breakpointObserver.isMatched(RESPONSIVE_BREAKPOINTS.tablet);
  }

  /**
   * Check if current viewport is desktop (synchronous)
   * @returns True if viewport is desktop
   */
  get isDesktop(): boolean {
    return this.breakpointObserver.isMatched(RESPONSIVE_BREAKPOINTS.desktop);
  }
}
